/**
 * SOUND PREFERENCES — the one record every audio surface reads, persisted to
 * the device only.
 *
 * ── WHY A DEVICE COPY AND NOTHING ELSE ──────────────────────────────────────
 * These are exactly the shape `engine/a11y/store.ts` reasons about at length:
 * a setting that must survive a reload of a player who is not signed in, so it
 * is written SYNCHRONOUSLY to `localStorage` and never blocks on a network. The
 * account half that store carries does not apply here — core's
 * `UpdatePreferencesDto` (`backend/src/core/i18n/preferences.controller.ts`)
 * declares only `locale` and `currency` and the global `ValidationPipe` runs
 * `forbidNonWhitelisted: true`, so a PATCH carrying `audio` is a 400, and the
 * DTO is under `backend/src/core/**` which HARD LAW forbids editing. A player's
 * volume is a per-device thing anyway (headphones on one machine, speakers on
 * another), so device-only is not a compromise here, it is the right scope.
 *
 * ── THE MUTE KEY IS SHARED WITH THE MATCH ───────────────────────────────────
 * `engine/match/audio/index.ts` has read `rga.audio.muted` since Phase 2
 * (`MUTE_STORAGE_KEY`, `readMutedSetting`). This module uses the SAME string so
 * the Settings mute toggle and the match engine's mute are ONE setting, not two
 * that drift. The three volumes are new keys beside it.
 *
 * ── EVERY READ AND WRITE IS GUARDED ─────────────────────────────────────────
 * Safari private mode owns a `localStorage` object and throws on write; an SSR
 * or locked-down iframe has none. Both must leave the setting in force (the
 * store still holds it for the session) and simply not remember it. So `storage`
 * probes once and every path is wrapped.
 */
import { createStore, type Store } from '@/core/state/store';

/* ═══════════════════════════════════════════════════════════════ shape ══ */

export interface AudioPrefs {
  /** Overall bus level, 0..1. Scales everything. */
  readonly master: number;
  /** Music bed level, 0..1, relative to master. */
  readonly music: number;
  /** Sound-effects level, 0..1, relative to master. */
  readonly sfx: number;
  /**
   * Interface sounds — clicks, toggles, chimes — 0..1, relative to master.
   * Its own slider because the one complaint every game with UI sound gets is
   * "I like the effects, the menu clicks drive me mad" (ui-sounds.ts).
   */
  readonly ui: number;
  /** The player asked for silence. Independent of the browser's autoplay lock. */
  readonly muted: boolean;
}

/**
 * Defaults chosen SANE, never loud (STYLE_BIBLE): music sits under the effects,
 * the effects sit under the master ceiling, and nothing is at 1.0. A first-time
 * player's first sound is comfortable, not a jump-scare.
 */
export const DEFAULT_AUDIO_PREFS: AudioPrefs = Object.freeze({
  master: 0.7,
  music: 0.5,
  sfx: 0.8,
  ui: 0.7,
  muted: false,
});

/** localStorage keys. `muted` is the one the match engine already reads. */
export const AUDIO_STORAGE_KEYS = Object.freeze({
  master: 'rga.audio.master',
  music: 'rga.audio.music',
  sfx: 'rga.audio.sfx',
  ui: 'rga.audio.ui',
  muted: 'rga.audio.muted',
});

/** The three volume fields, in the order the panel draws them. */
export const AUDIO_VOLUME_FIELDS = Object.freeze(['master', 'music', 'sfx', 'ui'] as const);
export type AudioVolumeField = (typeof AUDIO_VOLUME_FIELDS)[number];

/** Force a value into [0, 1]. NaN and negatives collapse to 0; >1 to 1. */
export function clampVolume(v: unknown): number {
  const n = typeof v === 'number' ? v : Number(v);
  return !(n > 0) ? 0 : n > 1 ? 1 : n;
}

/** Normalise any partial/loose record into a full, clamped `AudioPrefs`. */
export function normaliseAudioPrefs(raw: Partial<Record<keyof AudioPrefs, unknown>>): AudioPrefs {
  return Object.freeze({
    master: raw.master === undefined ? DEFAULT_AUDIO_PREFS.master : clampVolume(raw.master),
    music: raw.music === undefined ? DEFAULT_AUDIO_PREFS.music : clampVolume(raw.music),
    sfx: raw.sfx === undefined ? DEFAULT_AUDIO_PREFS.sfx : clampVolume(raw.sfx),
    ui: raw.ui === undefined || raw.ui === null ? DEFAULT_AUDIO_PREFS.ui : clampVolume(raw.ui),
    muted: raw.muted === undefined ? DEFAULT_AUDIO_PREFS.muted : raw.muted === true || raw.muted === '1',
  });
}

export function audioPrefsEqual(a: AudioPrefs, b: AudioPrefs): boolean {
  return a.master === b.master && a.music === b.music && a.sfx === b.sfx && a.ui === b.ui && a.muted === b.muted;
}

/* ═════════════════════════════════════════════════════════════ storage ══ */

/** `localStorage`, or null in private mode / SSR / a locked-down iframe. */
function storage(): Storage | null {
  try {
    const s = globalThis.localStorage;
    if (s === undefined || s === null) return null;
    const probe = '__rga_audio__';
    s.setItem(probe, '1');
    s.removeItem(probe);
    return s;
  } catch {
    return null;
  }
}

function readDevice(): AudioPrefs {
  const s = storage();
  if (s === null) return DEFAULT_AUDIO_PREFS;
  try {
    return normaliseAudioPrefs({
      master: s.getItem(AUDIO_STORAGE_KEYS.master) ?? undefined,
      music: s.getItem(AUDIO_STORAGE_KEYS.music) ?? undefined,
      sfx: s.getItem(AUDIO_STORAGE_KEYS.sfx) ?? undefined,
      ui: s.getItem(AUDIO_STORAGE_KEYS.ui) ?? undefined,
      // Only '1' is muted; absent or anything else is not.
      muted: s.getItem(AUDIO_STORAGE_KEYS.muted) === '1',
    });
  } catch {
    return DEFAULT_AUDIO_PREFS;
  }
}

function writeDevice(next: AudioPrefs): void {
  const s = storage();
  if (s === null) return;
  try {
    s.setItem(AUDIO_STORAGE_KEYS.master, String(next.master));
    s.setItem(AUDIO_STORAGE_KEYS.music, String(next.music));
    s.setItem(AUDIO_STORAGE_KEYS.sfx, String(next.sfx));
    s.setItem(AUDIO_STORAGE_KEYS.ui, String(next.ui));
    s.setItem(AUDIO_STORAGE_KEYS.muted, next.muted ? '1' : '0');
  } catch {
    /* quota or denied: in force for the session, simply not remembered */
  }
}

/* ═══════════════════════════════════════════════════════════════ state ══ */

export const audioPrefsStore: Store<AudioPrefs> = createStore<AudioPrefs>(DEFAULT_AUDIO_PREFS);

/** The current prefs. Cheap; safe in a render. */
export function audioPrefs(): AudioPrefs {
  return audioPrefsStore.get();
}

/**
 * Read the device copy into the store. Idempotent; call once before the first
 * audio surface reads. Returns the loaded prefs.
 */
export function initAudioPrefs(): AudioPrefs {
  const next = readDevice();
  audioPrefsStore.set(next);
  return next;
}

/** Replace the whole record: normalise, persist, publish. */
export function setAudioPrefs(next: Partial<Record<keyof AudioPrefs, unknown>>): void {
  const settings = normaliseAudioPrefs({ ...audioPrefsStore.get(), ...next });
  if (audioPrefsEqual(settings, audioPrefsStore.get())) return;
  writeDevice(settings);
  audioPrefsStore.set(settings);
}

/** Change one field. The form every control uses. */
export function updateAudioPrefs(patch: Partial<AudioPrefs>): void {
  setAudioPrefs(patch);
}

/** Test seam: forget everything. */
export function resetAudioPrefsForTests(): void {
  audioPrefsStore.set(DEFAULT_AUDIO_PREFS);
}
